import React, {
  ReactElement, useEffect, useState,
} from 'react';
import ReactDOM from 'react-dom';
import {
  Button, Col, Container, ModalBody, Row,
} from 'react-bootstrap';
import axios, { AxiosResponse } from 'axios';
import { FormikHelpers } from 'formik';
import { isUser, User } from '../../../structures/models/user';
import {
  SellerContactCreationRequest,
  SellerCreationRequest,
  SellerCreationRequestInProgress,
} from '../../../structures/requests/seller';
import { UserCreationRequest } from '../../../structures/requests/user';
import useOnScreen from '../../../hooks/useOnScreen';
import { Seller, UnsavedSellerContact } from '../../../structures/models/seller';
import useIsFirstRender from '../../../hooks/useIsFirstRender';
import useLocalStorage from '../../../hooks/useLocalStorage';
import { removePhoneFormatting } from '../../../utils/formatters';
import { BreedSelect } from '../../../structures/models/breed';
import {
  ApiV1Error, ApiV1Response, isCSRFError, isValidationError,
} from '../../../structures/responses/axios';
import routes from '../../../utils/routes';
import { LaravelValidationErrors } from '../../../structures/responses/errors';
import CompletionLeftPanel from './LeftPanel/CompletionLeftPanel';
import MainLeftPanel from './LeftPanel/MainLeftPanel';
import SellerConfirmationStep from './SellerConfirmationStep';
import SellerDetailsStep, { SellerDetails } from './SellerDetailsStep';
import UserRegistrationStep from './UserRegistrationStep';

export type FormValues = {
  step: number,
  savedUser?: User,
  seller: SellerCreationRequestInProgress,
  savedSeller?: Seller,
};

type FormStepObject = {
  component: React.ReactElement,
  leftPanel: React.ReactElement,
};

type setErrorFunc = (error: string) => void;

const initialSellerValues = (): SellerCreationRequestInProgress => {
  return {
    name: '',
    description: '',
    profile_description: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    phone: '',
    phone_extn: '',
    website: '',
    paypal: '',
    breeds: [],
    contacts: [],
    hours: [],
  };
};

const formatBreedsForSaving = (breeds: BreedSelect[]): number[] => {
  return breeds.map((i) => i.value);
};

const Register: React.FC<RegisterProps> = ({
  redirect,
  initialStep = 0,
  initialUser,
  finalStep,
  isModal = true,
  showLeftPanel = true,
}): ReactElement => {
  const userAddressInfo = (user: User|undefined) => {
    if (!user) {
      return {};
    }
    return {
      address_line_1: user.address_line_1 ? user.address_line_1 : '',
      address_line_2: user.address_line_2 ? user.address_line_2 : '',
      city: user.city ? user.city : '',
      state: user.state ? user.state : '',
      zip: user.zip ? user.zip : '',
    };
  };

  const [formValues, setFormValues] = useState<FormValues>({
    step: initialStep,
    seller: { ...initialSellerValues(), ...userAddressInfo(initialUser) },
    savedUser: initialUser,
  });

  const [contactsComplete, setContactsComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formMessage, setFormMessage] = useState<ReactElement[] | undefined>(undefined);

  // Define state for whether the component is currently visible
  const [ref, isVisible] = useOnScreen<HTMLDivElement>(true, 2000);
  // Is this the first render of the element?
  const isFirstRender = useIsFirstRender();
  // Local storage functionality
  const [setLocal, getLocal, removeLocal] = useLocalStorage<FormValues>('resumeRegistrationData');
  const [setShouldResume, removeShouldResume] = useLocalStorage<boolean>('shouldResumeRegistration');
  // Set submissionErrors, since Formik error bag resets when form is touched
  const [submissionErrors, setSubmissionErrors] = useState<LaravelValidationErrors>({});

  // Initialize component with previously filled out data, if it exists
  useEffect(() => {
    const resumeData = getLocal();

    // If user found in local storage differs from user passed to component, return early
    if (initialUser?.id !== resumeData?.savedUser?.id) {
      return;
    }

    const shouldResume = !isModal;

    if (resumeData && resumeData.seller) {
      resumeData.seller = { ...resumeData.seller, ...userAddressInfo(initialUser) };
    }

    if (resumeData && shouldResume) {
      resumeData.step = initialStep;
      setFormValues({ ...formValues, ...resumeData });
    }
  }, []);

  useEffect(() => {
    if (contactsComplete) {
      // Seller creation is complete. Move to confirmation step
      // and remove data from localStorage
      setFormValues({
        ...formValues,
        step: formValues.step + 1,
      });
      setIsLoading(false);
    }
  }, [contactsComplete]);

  const previousStep = (): void => {
    const { step } = formValues;

    if (step !== 0) {
      setFormValues({
        ...formValues,
        step: step - 1,
      });
    }
  };

  const getNextStepText = (isLast: boolean): string => {
    if (formValues.step === 0) {
      return 'Sign Up';
    }

    /* eslint-disable-next-line @typescript-eslint/no-use-before-define */
    if (isLast || formValues.step >= steps.length) {
      return 'Complete';
    }

    return 'Next';
  };

  const appendFormMessage = (element: ReactElement): void => {
    setFormMessage((current) => (current
      ? [...current, element]
      : [element]));
  };

  const onUserRegistrationSubmit = async (
    values: UserCreationRequest,
    {
      setErrors,
      setSubmitting,
    }: FormikHelpers<any>,
    setError: setErrorFunc,
  ): Promise<any> => {
    setSubmitting(true);
    setIsLoading(true);

    // Format for saving
    const userValues: UserCreationRequest = {
      ...values,
    };

    axios.post(routes.API_REGISTER, userValues, { headers: { accept: 'application/json' } })
      .then((res: AxiosResponse<{ user: User }>) => {
        if (res.status === 201) {
          if (values.role === 'seller') {
            setFormValues({
              ...formValues,
              seller: { ...formValues.seller, ...userAddressInfo(res.data.user) },
              step: formValues.step + 1,
              savedUser: res.data.user,
            });
            setSubmissionErrors({});
          } else {
            // User creation is finished. Remove data from
            // localStorage and redirect
            removeLocal();
            removeShouldResume();
            window.location.reload();
            setSubmissionErrors({});
          }
        }
      })
      .catch((error: ApiV1Error) => {
        if (isValidationError(error)) {
          setError('Please check your inputs and try again.');
          setErrors(error.response.data.errors);
          setSubmissionErrors(error.response.data.errors || {});
        } else if (isCSRFError(error)) {
          setError('This form must be refreshed.');
        } else {
          setError('An unknown error occurred. Please try again later.');
        }
      })
      .finally(() => {
        setSubmitting(false);
        setIsLoading(false);
      });
  };

  const submitSellerContacts = async (sellerId: number, contacts: UnsavedSellerContact[]) => {
    // Even though the form uses an array for contacts, currently, only one contact may be created
    const promises = contacts.map((contact) => {
      const contactToSave: SellerContactCreationRequest = {
        ...contact,
        seller_id: sellerId,
      };

      return axios.post(routes.API_SELLER_CONTACTS_POST, contactToSave);
    });

    return Promise.all(promises);
  };

  const submitSeller = async (sellerToSave: SellerCreationRequest) => {
    if (sellerToSave.id) {
      return axios.put(routes.apiSellerUpdate(sellerToSave.id), sellerToSave);
    }
    return axios.post(routes.API_SELLER_POST, sellerToSave);
  };

  const onSellerCreationSubmit = async (
    sellerValues: SellerDetails,
    {
      setErrors,
      setSubmitting,
    }: FormikHelpers<SellerDetails>,
    setError: setErrorFunc,
  ) => {
    const {
      savedUser,
      seller,
      savedSeller,
    } = formValues;
    setSubmitting(true);
    setIsLoading(true);

    if (!isUser(savedUser)) {
      throw new Error('No current user.');
    }

    // Format for saving
    const sellerToSave: SellerCreationRequest = {
      ...seller,
      ...sellerValues,
      breeds: formatBreedsForSaving(seller.breeds),
      phone: removePhoneFormatting(seller.phone),
      hours: [],
      user_id: savedUser.id,
      agreed_to_seller_terms: true,
    };

    if (savedSeller?.id) {
      sellerToSave.id = savedSeller.id;
    }

    await submitSeller(sellerToSave)
      .then(async (res: ApiV1Response<Seller>) => {
        if (res.status === 201) {
          setFormValues({
            ...formValues,
            savedSeller: res.data.data,
          });
        }

        await submitSellerContacts(res.data.data.id, sellerValues.contacts)
          .then(() => {
            setContactsComplete(true);
            setIsLoading(false);
            setSubmissionErrors({});
          })
          .catch((error: ApiV1Error) => {
            // Allow error to bubble
            throw error;
          });
      })
      .catch((error: ApiV1Error) => {
        // If seller upload fails, we are no longer loading
        setIsLoading(false);

        if (isValidationError(error)) {
          setError('Please check your inputs and try again:');
          setErrors(error.response.data.errors);
          setSubmissionErrors(error.response.data.errors || {});
        } else if (isCSRFError(error)) {
          setError('This form must be refreshed.');
          setSubmissionErrors({});
        } else {
          setError('An unknown error occurred. Please try again later.');
          setSubmissionErrors({});
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const renderFooter = (
    isValid: boolean = true,
    canGoBack: boolean = true,
    canGoNext: boolean = true,
    isLast: boolean = false,
  ): ReactElement => (
    <Row
      className={`bg-white justify-content-lg-between py-3 px-md-3 px-xl-0
        ${formValues.step === 0 ? 'flex-column-reverse flex-md-row align-items-center' : ''
      }`}
    >
      {(formValues.step === 0) && (
        <>
          <Col xs="auto" className="mt-3 mt-md-0 ml-xl-4">
            <a
              className="already-a-member"
              href="#"
              onClick={() => {
                // @ts-ignore
                $('#registerModal').modal('hide');
                // @ts-ignore
                $('#loginModal').modal('show');
              }}
            >
              Already a member? Log in
            </a>
          </Col>
        </>
      )}
      {canGoBack && (formValues.step > 0) ? (
        <Col xs="auto" className="text-center">
          <Button
            variant="outline-info"
            disabled={formValues.step <= 0 || isLoading}
            size="lg"
            onClick={previousStep}
          >
            Back
          </Button>
        </Col>
      ) : null}
      <Col xs="auto" className="text-center ml-auto">
        {canGoNext ? (
          <Button
            variant="primary"
            disabled={!isValid || isLoading}
            size="lg"
            type="submit"
            data-testid="register-next-step"
          >
            {getNextStepText(isLast)}
            {
              isLoading && (
                <span className="spinner-border spinner-border-sm my-auto ml-3">
                  <span className="sr-only">Loading...</span>
                </span>
              )
            }
          </Button>
        ) : null}
      </Col>
    </Row>
  );

  const steps: FormStepObject[] = [
    {
      component: <UserRegistrationStep
        onSubmit={onUserRegistrationSubmit}
        footer={renderFooter}
        hocState={formValues}
        key="user-registration"
      />,
      leftPanel: <MainLeftPanel hocState={formValues} />,
    },
    {
      component: <SellerDetailsStep
        onSubmit={onSellerCreationSubmit}
        onChange={(values) => {
          setFormValues(
            {
              ...formValues,
              seller: { ...formValues.seller, ...values },
            },
          );
        }}
        submissionErrors={submissionErrors}
        footer={renderFooter}
        hocState={formValues}
        key="seller-details-setup"
        appendFormMessage={appendFormMessage}
      />,
      leftPanel: <MainLeftPanel hocState={formValues} />,
    },
    {
      component: <SellerConfirmationStep
        completionRedirect={redirect}
        formMessage={formMessage}
        key="seller-confirmation-step"
        finalStep={finalStep}
      />,
      leftPanel: <CompletionLeftPanel hocState={formValues} />,
    },
  ];

  // Any time the form values or the visibility of the form changes, save the state
  // to localStorage. This allows us to re-initialize the form's old state
  // after refreshing or when the user re-opens the form.
  useEffect(() => {
    if (!isFirstRender) {
      if (isVisible) {
        setLocal(formValues);
        setShouldResume(true);
      } else {
        setShouldResume(false);
      }
    }
  }, [formValues, isVisible]);

  // When the final step of signup has been reached, remove the data from localStorage
  useEffect(() => {
    if (formValues.step === steps.length - 1) {
      removeLocal();
      removeShouldResume();
    }
  }, [formValues, steps]);

  const currentStep = (): FormStepObject => {
    return steps[formValues.step];
  };

  const getPanelColumns = () => {
    return (
      <>
        {(showLeftPanel || formValues.step === steps.length - 1) ? (
          <>
            <Col lg={4} className="px-0">
              {currentStep().leftPanel}
            </Col>
            <Col md={12} lg={8} className="pt-3">
              {currentStep().component}
            </Col>
          </>
        ) : (
          <Col xs={12} className="pt-3">
            {currentStep().component}
          </Col>
        )}
      </>
    );
  };

  return (
    <ModalBody className="p-0" ref={ref}>
      <Container fluid>
        {isModal && (
          <button type="button" className="close" data-dismiss="modal" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        )}
        <Row>
          {getPanelColumns()}
        </Row>
      </Container>
    </ModalBody>
  );
};

type RegisterProps = {
  redirect: string,
  initialStep?: number,
  initialUser?: User,
  finalStep?: number,
  isModal?: boolean,
  showLeftPanel?: boolean,
};

export default Register;

if (document.getElementById('registration-component')) {
  const element = document.getElementById('registration-component');
  const redirect = element?.dataset.redirect || routes.ADMIN_INDEX;
  const initialStep = Number(element?.dataset.initialStep) || undefined;
  const finalStep = Number(element?.dataset.finalStep) || undefined;
  const initialUser = element?.dataset.initialUser
    ? JSON.parse(element.dataset.initialUser)
    : undefined;
  const isModal = element?.dataset.isModal
    ? element.dataset.isModal === 'true'
    : undefined;
  const showLeftPanel = element?.dataset.showLeftPanel
    ? element.dataset.showLeftPanel === 'true'
    : undefined;

  ReactDOM.render(<Register
    redirect={redirect}
    initialStep={initialStep}
    initialUser={initialUser}
    finalStep={finalStep}
    isModal={isModal}
    showLeftPanel={showLeftPanel}
  />, document.getElementById('registration-component'));
}
